<template>
    <DCard
        :title="__('orders.show.order_details')"
        :main-title-has-slot="true"
        padding
    >
        <template #titleHasSlot>
            <DSwitch
                color="success"
                variant="3d"
                :disabled="isInLoadingState"
                :checked="showSection"
                @update:checked="showCardSection"
            />
        </template>
        <DRow v-if="showSection">
            <DCol col="6">
                <DFormRow :label="__('orders.show.order_number')">
                    {{ order.orderNumber }} ({{ __('orders.show.id_internal') }} :{{ order.idInternal }})
                    <IncidentFlag
                        v-if="userPermissions.includes('customer.incidents_view') && order.hasIncidents"
                        id="order-incident-flag"
                        class="ms-2"
                        :active-incident="order.hasOpenIncidents"
                        :relation-type="'orders'"
                        :relation-id="order.idInternal"
                        :customer-id="order.databaseCustomerId"
                    />
                    <span
                        v-if="order.pdfDocument !== null"
                        class="p-2"
                    >
                        <DButton
                            id="download-pdf"
                            icon="download"
                            :mini="true"
                            :disabled="isInLoadingState"
                            :label="__('orders.show.download_pdf')"
                            @click="downloadPdf"
                        />
                    </span>
                </DFormRow>
                <DFormRow :label="__('orders.show.order_client')">
                    {{ order.client }}
                </DFormRow>
                <DFormRow :label="__('orders.show.status')">
                    <DTag
                        v-if="order.status !== null"
                        :label="__('orders.status_'+order.status)"
                    />
                </DFormRow>
                <DFormRow :label="__('orders.show.external_note')">
                    <div
                        v-if="order.externalNote"
                        class="text-break"
                    >
                        <span style="white-space: pre-line">{{ order.externalNote }}</span>
                    </div>
                    <DTag
                        v-else
                        :label="__('orders.show.no_note')"
                        :color="'danger'"
                    />
                </DFormRow>
            </DCol>
            <DCol col="6">
                <DFormRow :label="__('orders.show.id_internal')">
                    {{ order.idInternal }}
                </DFormRow>
                <DFormRow :label="__('orders.show.entry_date')">
                    {{ setDateFormat(order.entryDate) }}
                </DFormRow>
                <DFormRow :label="__('orders.show.internal_note')">
                    <div
                        v-if="order.internalNote !== null"
                        class="text-break text-space-wrap"
                    >
                        {{ order.internalNote.note }}
                    </div>
                    <DTag
                        v-else
                        :label="__('orders.show.no_note')"
                        :color="'danger'"
                    />
                </DFormRow>
                <DFormRow
                    v-if="order?.ssnBusinessId"
                    :label="__('orders.show.ssn_business_id')"
                >
                    {{ order.ssnBusinessId }}
                </DFormRow>
            </DCol>
        </DRow>
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { useForm, usePage } from '@inertiajs/vue3'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'

const props = defineProps({
    order: {
        type: Object,
        default () {
            return {}
        }
    },
    section: {
        type: String,
        default () {
            return ''
        }
    },
    showSection: {
        type: Boolean,
        default () {
            return true
        }
    },
    userListView: {
        type: Array,
        default: () => {
            return []
        }
    }
})

function downloadPdf () {
    window.open(props.order.pdfDocument, '_blank')
}

const emit = defineEmits(['update-list'])

const columns = ref(props.userListView)

const form = useForm({
    columns: props.userListView
})

const userPermissions = computed(() => usePage().props.auth.permissions)

function showCardSection (value) {
    for (const column of columns.value) {
        if (column.column === props.section) {
            column.show_column = value
        }
    }

    form.columns = flattenColumns(columns.value)

    form.post(route('settings.list-view-settings.store'))

    emit('update-list', columns.value)
}

function flattenColumns (columns) {
    if (columns.length === 0) {
        return {}
    }

    const result = {}

    for (const [key, column] of Object.entries(columns)) {
        result[key] = JSON.parse(JSON.stringify(column))
    }

    return result
}
</script>

<style lang="scss" scoped>
.text-space-wrap {
    white-space: pre-wrap;
}
</style>
